// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb';
import { QueryOperator } from '../operators/QueryOperator';
import { MutationOperator } from '../operators/MutationOperator';
import type { QueryOptions } from '../operators/types';
import type { WorkspaceModel } from '../../models/Workspace';
import { createId } from '../../index';

export interface WorkspaceFilters {
  /** Organization ID (optional - omit for cross-org queries in "View All" mode) */
  orgId?: string;
  /** Filter by user ID */
  userId?: string;
  /** Filter by workspace status */
  status?: WorkspaceModel['status'];
  /** Filter by archived state (default: false) */
  archived?: boolean;
}

export interface CreateWorkspaceInput {
  /** Workspace name (kebab-case) */
  name: string;
  /** Workspace title */
  title: string;
  /** Workspace description */
  description: string;
  /** Organization ID */
  orgId: string;
  /** User ID of workspace creator */
  userId: string;
  /** Optional workspace status (default: 'active') */
  status?: WorkspaceModel['status'];
  /** Optional tags */
  tags?: string[];
}

export interface UpdateWorkspaceInput {
  /** Workspace ID to update */
  id: string;
  /** Partial workspace data to update */
  updates: Partial<WorkspaceModel>;
}

export class WorkspaceOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: WorkspaceFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<WorkspaceModel> {
    const collection = this.db.collections.workspaces;

    // Build selector - orgId is optional to support "View All" mode across organizations
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    // Only add orgId filter if provided (allows cross-org queries when not specified)
    if (filters.orgId) {
      selector.orgId = { $eq: filters.orgId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.status) {
      selector.status = { $eq: filters.status };
    }

    const queryOptions: QueryOptions<WorkspaceModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    };

    return new QueryOperator<WorkspaceModel>(collection, queryOptions);
  }

  async findOne(workspaceId: string): Promise<WorkspaceModel | null> {
    const doc = await this.db.collections.workspaces.findOne(workspaceId).exec();
    return doc ? (doc.toJSON() as WorkspaceModel) : null;
  }

  async count(filters: WorkspaceFilters): Promise<number> {
    const collection = this.db.collections.workspaces;

    // Build selector - orgId is optional to support "View All" mode
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    if (filters.orgId) {
      selector.orgId = { $eq: filters.orgId };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    if (filters.status) {
      selector.status = { $eq: filters.status };
    }

    return await collection.count({ selector }).exec();
  }

  createMutation(): MutationOperator<WorkspaceModel, CreateWorkspaceInput> {
    return new MutationOperator<WorkspaceModel, CreateWorkspaceInput>(
      this.db.collections.workspaces,
      {
        mutationFn: async (collection, input) => {
          const now = new Date().toISOString();
          const workspace: WorkspaceModel = {
            id: createId('workspace'),
            orgId: input.orgId,
            userId: input.userId,
            name: input.name,
            title: input.title,
            description: input.description,
            status: input.status || 'active',
            archived: false,
            createdAt: now,
            updatedAt: now,
            ...(input.tags && { tags: input.tags }),
          };

          await collection.insert(workspace);
          return workspace;
        },
      }
    );
  }

  updateMutation(): MutationOperator<WorkspaceModel, UpdateWorkspaceInput> {
    return new MutationOperator<WorkspaceModel, UpdateWorkspaceInput>(
      this.db.collections.workspaces,
      {
        mutationFn: async (collection, { id, updates }) => {
          const doc = await collection.findOne(id).exec();
          if (!doc) throw new Error(`Workspace ${id} not found`);

          await doc.update({
            $set: { ...updates, updatedAt: new Date().toISOString() },
          });

          return doc.toJSON() as WorkspaceModel;
        },
      }
    );
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.workspaces, {
      mutationFn: async (collection, workspaceId) => {
        const doc = await collection.findOne(workspaceId).exec();
        if (!doc) throw new Error(`Workspace ${workspaceId} not found`);

        await doc.update({
          $set: { archived: true, updatedAt: new Date().toISOString() },
        });
      },
    });
  }
}
